"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useCallback, useRef, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Menu, Search, MapPin, ShoppingCart, User, X, ChevronRight, Phone, Mail } from "lucide-react";
import { usePostalCode } from "@/lib/postal-code-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MegaMenu } from "./MegaMenu";
import { MobileMenu } from "./MobileMenu";
import { TopBanner } from "./TopBanner";
import { QuickNavSection } from "@/components/sections/QuickNavSection";
import { SearchModal } from "@/components/SearchModal";
import { useCart } from "@/lib/cart-context";
import type { Product, Category } from "@/lib/db";

const navItems = [
  { name: "Location de matériel", href: "/categories" },
  { name: "Achat de matériel", href: "/categories" },
  { name: "Toutes les catégories", href: "/categories" },
];

const HIDDEN_QUICKNAV_PATHS = ["/panier", "/checkout", "/mon-compte", "/connexion"];

export function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const { cartCount } = useCart();
  const { postalCode, setPostalCode, hasPostalCode } = usePostalCode();
  const showQuickNav = !HIDDEN_QUICKNAV_PATHS.some((p) => pathname.startsWith(p));
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [cpInput, setCpInput] = useState("");
  const [cpError, setCpError] = useState<string | null>(null);
  const [showCpDropdown, setShowCpDropdown] = useState(false);
  const [showCpBanner, setShowCpBanner] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [editingMobileCp, setEditingMobileCp] = useState(false);
  const cpDropdownRef = useRef<HTMLDivElement>(null);
  const [searchResults, setSearchResults] = useState<{
    products: Product[];
    categories: Category[];
  }>({ products: [], categories: [] });
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [user, setUser] = useState<{ first_name: string; last_name: string } | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const desktopSearchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);

  // Mark as mounted to avoid hydration mismatches on dynamic content
  useEffect(() => { setMounted(true); }, []);

  // Close search on route change
  const pathnameStr = pathname || "";
  useEffect(() => {
    setIsSearchOpen(false);
  }, [pathnameStr]);

  // Close search on click outside search containers
  useEffect(() => {
    if (!isSearchOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inDesktop = desktopSearchRef.current?.contains(target);
      const inMobile = mobileSearchRef.current?.contains(target);
      if (!inDesktop && !inMobile) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isSearchOpen]);

  // Check if user is logged in — re-check on route change (e.g. after login)
  useEffect(() => {
    fetch("/api/auth/me")
      .then((res) => {
        if (res.ok) return res.json();
        return null;
      })
      .then((data) => {
        setUser(data?.user || null);
      })
      .catch(() => setUser(null));
  }, [pathnameStr]);

  // Fetch search results
  const fetchSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setSearchResults({ products: [], categories: [] });
      setIsSearchLoading(false);
      return;
    }

    setIsSearchLoading(true);
    setIsSearchOpen(true);

    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults({
          products: data.products || [],
          categories: data.categories || [],
        });
      }
    } catch (error) {
      console.error("Search fetch error:", error);
    } finally {
      setIsSearchLoading(false);
    }
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Always keep modal open while typing
      setIsSearchOpen(true);

      if (value.trim().length < 2) {
        setSearchResults({ products: [], categories: [] });
        setIsSearchLoading(false);
        return;
      }

      setIsSearchLoading(true);

      debounceTimerRef.current = setTimeout(() => {
        fetchSearch(value.trim());
      }, 300);
    },
    [fetchSearch]
  );

  // Handle Enter key or search button click — navigate to search results page
  const handleSearchSubmit = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setIsSearchOpen(false);
    const q = searchQuery.trim();
    if (q.length >= 2) {
      router.push(`/recherche?q=${encodeURIComponent(q)}`);
    }
  }, [searchQuery, router]);

  // Handle key press on input
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSearchSubmit();
      }
    },
    [handleSearchSubmit]
  );

  // Close search modal
  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setSearchResults({ products: [], categories: [] });
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  // Handle postal code submit
  const handleCpSubmit = useCallback(() => {
    const error = setPostalCode(cpInput);
    if (error) {
      setCpError(error);
      return;
    }
    setCpError(null);
    setCpInput("");
    setShowCpDropdown(false);
    setShowCpBanner(false);
    setEditingMobileCp(false);
  }, [cpInput, setPostalCode]);

  // Close CP dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (cpDropdownRef.current && !cpDropdownRef.current.contains(e.target as Node)) {
        setShowCpDropdown(false);
      }
    }
    if (showCpDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showCpDropdown]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Bandeau promo */}
      <TopBanner />

      {/* Main header - Vert foncé */}
      <div className="bg-secondary">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 gap-4">
            {/* Logo */}
            <Link href="/" className="flex-shrink-0">
              <Image
                src="/e59ec9a9_logo2.18b3d9b3.svg"
                alt="Toolouer"
                width={160}
                height={45}
                className="h-10 w-auto"
              />
            </Link>

            {/* Search bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-2xl" ref={desktopSearchRef}>
              <div className="relative w-full">
                <Input
                  type="text"
                  placeholder="Rechercher un produit"
                  className="pl-4 pr-12 w-full bg-white rounded-full h-11"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setIsSearchOpen(true)}
                  suppressHydrationWarning
                />
                <Button
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full h-9 w-9 bg-primary hover:bg-primary/90"
                  onClick={handleSearchSubmit}
                >
                  <Search className="h-4 w-4" />
                </Button>

                {/* Search Modal - Desktop */}
                {isSearchOpen && (
                  <div
                    tabIndex={-1}
                    onMouseDown={(e) => {
                      // Always preventDefault to keep input focused.
                      // Links will still navigate via their onClick handler.
                      e.preventDefault();
                    }}
                  >
                    <SearchModal
                      query={searchQuery}
                      isOpen={isSearchOpen}
                      onClose={closeSearch}
                      products={searchResults.products}
                      categories={searchResults.categories}
                      isLoading={isSearchLoading}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Location selector */}
              <div className="relative" ref={cpDropdownRef}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden sm:flex gap-1 text-white hover:bg-white/10 cursor-pointer"
                  onClick={() => { setShowCpDropdown(!showCpDropdown); setCpInput(""); setCpError(null); }}
                >
                  <MapPin className="h-4 w-4" />
                  <span className="text-xs">{mounted && hasPostalCode ? postalCode : "Mon CP"}</span>
                </Button>

                {showCpDropdown && (
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border p-4 w-64 z-50">
                    <p className="text-sm font-medium text-gray-700 mb-2">Votre code postal</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        placeholder="Ex: 75 ou 75011"
                        value={cpInput}
                        onChange={(e) => { setCpInput(e.target.value.replace(/\D/g, "").substring(0, 5)); setCpError(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter") handleCpSubmit(); }}
                        className={`flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary/50 ${cpError ? "border-red-400" : "border-gray-300"}`}
                        autoFocus
                      />
                      <button
                        onClick={handleCpSubmit}
                        className="bg-secondary text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-secondary/90"
                      >
                        OK
                      </button>
                    </div>
                    {cpError && (
                      <p className="text-xs text-red-500 mt-2">{cpError}</p>
                    )}
                    {mounted && hasPostalCode && !cpError && (
                      <p className="text-xs text-gray-500 mt-2">Actuel : {postalCode}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Connection */}
              {mounted && user ? (
                <Link href="/mon-compte">
                  <Button variant="ghost" size="sm" className="hidden sm:flex gap-2 text-white hover:bg-white/10 cursor-pointer">
                    <User className="h-4 w-4" />
                    <span>Mon compte</span>
                  </Button>
                </Link>
              ) : (
                <Link href="/connexion">
                  <Button variant="ghost" size="sm" className="hidden sm:flex gap-2 text-white hover:bg-white/10 cursor-pointer">
                    <User className="h-4 w-4" />
                    <span>Se connecter</span>
                  </Button>
                </Link>
              )}

              {/* Cart */}
              <Link href="/panier">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 cursor-pointer relative">
                  <ShoppingCart className="h-5 w-5" />
                  {mounted && cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {cartCount > 99 ? "99+" : cartCount}
                    </span>
                  )}
                </Button>
              </Link>

              {/* Mobile menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={(open) => { setMobileMenuOpen(open); if (!open) setEditingMobileCp(false); }}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden text-white hover:bg-white/10">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[320px] p-0 overflow-y-auto [&>button:last-of-type]:hidden" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <SheetTitle className="sr-only">Menu</SheetTitle>

                  {/* Header with logo + close */}
                  <div className="px-5 pt-4 pb-3 border-b flex items-center justify-between">
                    <Image src="/logo_green_font.png" alt="Toolouer" width={110} height={28} className="h-6 w-auto" />
                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                    >
                      <X className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>

                  {/* Categories navigation */}
                  <div className="px-4 py-3 flex-1">
                    <MobileMenu onNavigate={() => setMobileMenuOpen(false)} />
                  </div>

                  {/* Postal code */}
                  <div className="px-5 py-3 border-t bg-gray-50">
                    {mounted && hasPostalCode && !editingMobileCp ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-secondary" />
                          <div>
                            <p className="text-xs text-gray-500">Code postal</p>
                            <p className="text-sm font-semibold">{postalCode}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => { setEditingMobileCp(true); setCpInput(""); setCpError(null); }}
                          className="text-xs font-medium text-secondary hover:underline"
                        >
                          Modifier
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="h-4 w-4 text-secondary" />
                          <p className="text-xs font-medium text-gray-600">Votre code postal</p>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={5}
                            placeholder="Ex: 75011"
                            value={cpInput}
                            onChange={(e) => { setCpInput(e.target.value.replace(/\D/g, "").substring(0, 5)); setCpError(null); }}
                            onKeyDown={(e) => { if (e.key === "Enter") handleCpSubmit(); }}
                            className={`flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary/50 ${cpError ? "border-red-400" : "border-gray-300"}`}
                          />
                          <button
                            onClick={handleCpSubmit}
                            className="bg-secondary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-secondary/90"
                          >
                            OK
                          </button>
                        </div>
                        {cpError && (
                          <p className="text-xs text-red-500 mt-1">{cpError}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* User + links */}
                  <div className="border-t px-5 py-3 space-y-1">
                    {mounted && user ? (
                      <Link
                        href="/mon-compte"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 py-2 hover:text-secondary transition-colors"
                      >
                        <User className="h-4 w-4 text-secondary" />
                        <span className="text-sm font-medium">{user.first_name} {user.last_name}</span>
                        <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                      </Link>
                    ) : (
                      <Link
                        href="/connexion"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 py-2 hover:text-secondary transition-colors"
                      >
                        <User className="h-4 w-4 text-secondary" />
                        <span className="text-sm font-medium">Se connecter</span>
                        <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                      </Link>
                    )}
                    <Link
                      href="/contact-us"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 py-2 text-sm text-gray-600 hover:text-secondary transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      Contactez-nous
                    </Link>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* Search bar - Mobile */}
          <div className="md:hidden pb-3" ref={mobileSearchRef}>
            <div className="relative w-full">
              <Input
                type="text"
                placeholder="Rechercher..."
                className="pl-4 pr-12 w-full bg-white rounded-full"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                onFocus={(e) => {
                  setIsSearchOpen(true);
                  const input = e.currentTarget;
                  requestAnimationFrame(() => {
                    input.selectionStart = input.selectionEnd = input.value.length;
                  });
                }}
                suppressHydrationWarning
              />
              <Button
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full h-8 w-8 bg-primary hover:bg-primary/90"
                onClick={handleSearchSubmit}
              >
                <Search className="h-4 w-4" />
              </Button>

              {/* Search Modal - Mobile */}
              {isSearchOpen && (
                <div
                  tabIndex={-1}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                >
                  <SearchModal
                    query={searchQuery}
                    isOpen={isSearchOpen}
                    onClose={closeSearch}
                    products={searchResults.products}
                    categories={searchResults.categories}
                    isLoading={isSearchLoading}

                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="md:hidden flex flex-wrap items-center justify-center gap-x-4 gap-y-1 bg-secondary px-3 py-2 text-xs text-white">
        <a href="tel:+33189293489" className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />01 89 29 34 89</a>
        <a href="mailto:groupe@toolouer.com" className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />groupe@toolouer.com</a>
      </div>

      {/* Navigation - Desktop */}
      <nav className="hidden md:block bg-secondary border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-6 h-11">
            <MegaMenu type="location" />
            <MegaMenu type="achat" />
            <MegaMenu type="acte-administratif" />
            <a
              href="tel:+33189293489"
              className="ml-auto flex items-center gap-1.5 text-white/80 hover:text-white transition-colors text-sm"
            >
              <Phone className="h-3.5 w-3.5" />
              <span>01 89 29 34 89</span>
            </a>
            <a href="mailto:groupe@toolouer.com" className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm">
              <Mail className="h-3.5 w-3.5" />
              <span>groupe@toolouer.com</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Quick category navigation */}
      {showQuickNav && <QuickNavSection />}

      {/* Postal code banner — shown until user enters a CP */}
      {mounted && !hasPostalCode && showCpBanner && (
        <div className="border-b border-amber-200 bg-[#fffaf0] md:bg-amber-50">
          <div className="container mx-auto px-4 py-3 md:py-2.5">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1 md:flex md:items-center md:gap-2">
                <div className="hidden items-center gap-2 md:flex">
                  <MapPin className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  <span className="text-sm text-amber-800">
                    Renseignez votre code postal pour voir la disponibilité
                  </span>
                </div>
                <div className="flex w-full gap-2 md:ml-auto md:w-auto">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="Code postal"
                    aria-label="Votre code postal"
                    value={cpInput}
                    onChange={(e) => { setCpInput(e.target.value.replace(/\D/g, "").substring(0, 5)); setCpError(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") handleCpSubmit(); }}
                    className={`min-w-0 flex-1 border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary/50 bg-white md:w-28 md:flex-none md:py-1.5 ${cpError ? "border-red-400" : "border-amber-300"}`}
                  />
                  <button
                    onClick={handleCpSubmit}
                    className="flex flex-shrink-0 items-center gap-1 rounded-lg bg-secondary px-4 py-2.5 text-sm font-medium text-white hover:bg-secondary/90 md:px-3 md:py-1.5"
                  >
                    Valider
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <button onClick={() => setShowCpBanner(false)} className="flex-shrink-0 text-amber-400 hover:text-amber-600 md:mt-0.5">
                <X className="h-4 w-4" />
              </button>
            </div>
            {cpError && (
              <p className="text-xs text-red-500 mt-1">{cpError}</p>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
